import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, Search, Filter, Clock, ArrowRight, Heart } from 'lucide-react';
import { EvenementsService, type Evenement as EvenementType } from '../../services/evenements.service';
import { getImageUrl } from '../../services/api';
import { Helmet } from 'react-helmet';

const Evenement: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [evenements, setEvenements] = useState<EvenementType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvenements();
  }, []);

  const loadEvenements = async () => {
    try {
      setLoading(true);
      const response = await EvenementsService.findAll(1, 100);
      const data = Array.isArray(response) ? response : response.data;
      setEvenements(data);

      // Track views for all events on page load
      data.forEach(async (event) => {
        try {
          await EvenementsService.incrementView(event.id);
        } catch (err) {
          // Silently fail view tracking to not disrupt user experience
          console.error('Failed to track view for event', event.id, err);
        }
      });
    } catch (err) {
      console.error('Failed to load evenements', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (eventId: string) => {
    try {
      await EvenementsService.incrementLike(eventId);
      // Reload events to show updated like count
      loadEvenements();
    } catch (err) {
      console.error('Failed to like event', err);
    }
  };

  const filteredEvents = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    return evenements.filter(event => {
      const dateDebut = new Date(event.dateDebut);
      const dateFin = new Date(event.dateFin);
      dateFin.setHours(0, 0, 0, 0);
      dateDebut.setHours(0, 0, 0, 0);

      if (activeTab === 'upcoming') {
        return dateDebut >= now;
      } else {
        return dateFin < now;
      }
    }).sort((a, b) => {
      const dateA = new Date(a.dateDebut).getTime();
      const dateB = new Date(b.dateDebut).getTime();
      return activeTab === 'upcoming' ? dateA - dateB : dateB - dateA;
    });
  }, [evenements, activeTab]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number]
      }
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>Événements - SAPI</title>
      </Helmet>

      {/* Header Section */}
      <div className="bg-white pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-2">ÉVÉNEMENTS</p>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Nos Événements
            </h1>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex justify-center w-full">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`py-3 px-8 transition-all duration-300 font-medium text-base border-2 ${activeTab === 'upcoming'
                ? 'bg-primary text-white border-primary shadow-md'
                : 'bg-white text-gray-700 border-gray-200 hover:border-secondary hover:text-secondary'
                }`}
            >
              Événements à venir
            </button>
            <button
              onClick={() => setActiveTab('past')}
              className={`py-3 px-8 transition-all duration-300 font-medium text-base border-2 ${activeTab === 'past'
                ? 'bg-primary text-white border-primary shadow-md'
                : 'bg-white text-gray-700 border-gray-200 hover:border-secondary hover:text-secondary'
                }`}
            >
              Événements passés
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-20">
        {/* Events Grid */}
        {loading ? (
          <div className="py-20 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600">Chargement des événements...</p>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <AnimatePresence mode="wait">
              {filteredEvents.length > 0 ? (
                filteredEvents.map((event, index) => {
                  const dateDebut = new Date(event.dateDebut);
                  const day = dateDebut.getDate().toString().padStart(2, '0');
                  const month = dateDebut.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase();

                  return (
                    <motion.div
                      key={event.id}
                      variants={cardVariants}
                      layout
                      whileHover={{ y: -8 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer group relative"
                      onClick={() => navigate(`/Evenement/${event.id}`)}
                    >
                      {/* Image Section */}
                      <div className="relative h-[500px] w-full overflow-hidden">
                        {event.image ? (
                          <img
                            src={getImageUrl(event.image)}
                            alt={event.titre}
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary/10 via-secondary/10 to-gray-100 flex items-center justify-center">
                            <Calendar className="h-20 w-20 text-primary/30" />
                          </div>
                        )}

                        {/* Date Badge - Top Right */}
                        <div className="absolute top-4 right-4 bg-white px-4 py-3 shadow-lg z-10">
                          <div className="flex flex-col items-center">
                            <span className="text-3xl font-bold text-primary leading-none">{day}</span>
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mt-1">{month}</span>
                          </div>
                        </div>

                        {/* Gradient Overlay for Title Area */}
                        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none"></div>

                        {/* Title Section - Glassmorphism Effect */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 backdrop-blur-md bg-white/30 border-t border-white/20">
                          <h3 className="text-lg font-bold text-white leading-tight group-hover:text-secondary transition-colors line-clamp-3 drop-shadow-lg">
                            {event.titre}
                          </h3>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="col-span-full py-20 text-center bg-gray-50"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white shadow-sm mb-4">
                    <Search className="h-6 w-6 text-gray-300" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Aucun événement trouvé</h3>
                  <p className="text-gray-500 text-sm">Il n'y a aucun événement à afficher pour le moment.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Evenement;
